import { useEffect, useMemo, useState } from 'react';
import { Props as ReactApexChartProps } from 'react-apexcharts';
import { Button } from '../../../../../../components/button/button';
import type { ExternalMonitoringPeriodBounds } from '../../../../../../helpers/external-monitoring-period';
import { ChartProps } from '../../dashboard-components/dashboard-charts/use-dashboard-charts';
import { fillColorFromPositiveNegative } from './heatmap-sentiment-palettes';
import { EMPTY_PYRAMID_API, type PyramidChartApi } from './use-aspects-pyramid';
import s from './use-aspects-sentiment-master-detail.module.css';

const POSITIVE_COLOR = '#12B76A';
const NEGATIVE_COLOR = '#F04438';
const ALL_CATEGORIES_ID = '__all__';

type CategorySummary = {
  id: string;
  name: string;
  positive: number;
  negative: number;
  aspectsCount: number;
};

export const useAspectsSentimentMasterDetail = (
  api?: PyramidChartApi,
  _period?: ExternalMonitoringPeriodBounds,
): [ChartProps, ChartProps] => {
  const source = api ?? EMPTY_PYRAMID_API;
  const { categories, aspects } = source;
  const categoryNameById = useMemo(
    () => Object.fromEntries(categories.map((x) => [x.id, x.name])),
    [categories],
  );

  const categorySummary = useMemo<CategorySummary[]>(() => {
    const byId = new Map<string, CategorySummary>();
    for (const c of categories) {
      byId.set(c.id, { id: c.id, name: c.name, positive: 0, negative: 0, aspectsCount: 0 });
    }
    for (const a of aspects) {
      const existing = byId.get(a.categoryId);
      if (!existing) continue;
      existing.positive += Number(a.positive ?? 0);
      existing.negative += Number(a.negative ?? 0);
      existing.aspectsCount += 1;
    }
    return Array.from(byId.values())
      .filter((x) => x.aspectsCount > 0)
      .sort((a, b) => b.aspectsCount - a.aspectsCount);
  }, [aspects, categories]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  useEffect(() => {
    if (categorySummary.length === 0) {
      setSelectedCategoryId(null);
      return;
    }
    if (
      selectedCategoryId &&
      (selectedCategoryId === ALL_CATEGORIES_ID ||
        categorySummary.some((x) => x.id === selectedCategoryId))
    ) {
      return;
    }
    setSelectedCategoryId(categorySummary[0].id);
  }, [categorySummary, selectedCategoryId, _period?.begin, _period?.end]);

  const masterChartData = useMemo<ReactApexChartProps>(() => {
    const selected = selectedCategoryId;
    const labels = categorySummary.map((x) => x.name);
    const positiveSeries = categorySummary.map((x) => x.positive);
    const negativeSeries = categorySummary.map((x) => -x.negative);
    const colors = categorySummary.map((x) => (x.id === selected ? '#111827' : '#98A2B3'));

    return {
      series: [
        { name: 'Положительные', data: positiveSeries },
        { name: 'Негативные', data: negativeSeries },
      ],
      options: {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
          events: {
            dataPointSelection: (_event, _ctx, config) => {
              const idx = config.dataPointIndex;
              if (idx < 0 || idx >= categorySummary.length) return;
              setSelectedCategoryId(categorySummary[idx].id);
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '72%',
            borderRadius: 4,
          },
        },
        xaxis: {
          categories: labels,
          labels: {
            formatter: (value: string) => String(Math.abs(Math.round(Number(value)))),
          },
          title: { text: 'Количество' },
        },
        yaxis: {
          labels: {
            style: { colors },
          },
        },
        colors: [POSITIVE_COLOR, NEGATIVE_COLOR],
        legend: { position: 'top' },
        grid: { xaxis: { lines: { show: false } } },
        tooltip: {
          y: {
            formatter: (value: number) => String(Math.abs(value)),
          },
        },
        dataLabels: { enabled: false },
      },
    };
  }, [categorySummary, selectedCategoryId]);

  const detailChartData = useMemo<ReactApexChartProps>(() => {
    if (!selectedCategoryId) {
      return {
        series: [],
        options: {
          chart: { type: 'treemap', toolbar: { show: false } },
        },
      };
    }

    const isAllCategoriesMode = selectedCategoryId === ALL_CATEGORIES_ID;
    const selectedCategory = categories.find((x) => x.id === selectedCategoryId);
    const selectedAspects = isAllCategoriesMode
      ? aspects
      : aspects.filter((x) => x.categoryId === selectedCategoryId);

    const byAspect = new Map<string, { x: string; pos: number; neg: number }>();
    for (const item of selectedAspects) {
      const label = isAllCategoriesMode
        ? `${categoryNameById[item.categoryId] ?? item.categoryId} — ${item.name}`
        : item.name;
      const positive = Number(item.positive ?? 0);
      const negative = Number(item.negative ?? 0);
      const weight = positive + negative;
      if (weight <= 0) continue;

      const existing = byAspect.get(label) ?? { x: label, pos: 0, neg: 0 };
      existing.pos += positive;
      existing.neg += negative;
      byAspect.set(label, existing);
    }

    const treemapData = Array.from(byAspect.values()).map((row) => ({
      x: row.x,
      y: row.pos + row.neg,
      fillColor: fillColorFromPositiveNegative(row.pos, row.neg),
    }));

    const series = [{ name: 'Аспекты', data: treemapData }];

    return {
      series,
      options: {
        chart: { type: 'treemap', toolbar: { show: false } },
        title: {
          text: isAllCategoriesMode
            ? 'Детализация: Все категории'
            : `Детализация: ${selectedCategory?.name ?? ''}`,
          align: 'left',
          style: { fontSize: '13px', fontWeight: '600' },
        },
        plotOptions: {
          treemap: {
            distributed: true,
            enableShades: false,
          },
        },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          style: { fontSize: '10px' },
        },
        tooltip: {
          y: {
            formatter: (val: number) => String(Math.round(val)),
          },
        },
      },
    };
  }, [aspects, categories, selectedCategoryId, categoryNameById]);

  const controls = (
    <div className={s.footer}>
      <div className={s.categorySwitch}>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          outline
          className={selectedCategoryId === ALL_CATEGORIES_ID ? s.switchBtnActive : undefined}
          onClick={() => setSelectedCategoryId(ALL_CATEGORIES_ID)}
        >
          Все категории
        </Button>
        {categorySummary.map((category) => (
          <Button
            key={category.id}
            type="button"
            variant="secondary"
            size="sm"
            outline
            className={selectedCategoryId === category.id ? s.switchBtnActive : undefined}
            onClick={() => setSelectedCategoryId(category.id)}
          >
            {category.name}
          </Button>
        ))}
      </div>
    </div>
  );

  const masterCard: ChartProps = {
    ...masterChartData,
    label: 'Распределение оценок по категориям',
    labelTooltip:
      'Отображает количественное распределение позитивных и негативных оценок аспектов в разрезе каждой категории. Длина полос позволяет сопоставить общую частоту упоминания аспектов и определить фактический объем критических или одобрительных замечаний.',
    labelInfoIcon: true,
    type: 'bar',
  };

  const detailCard: ChartProps = {
    ...detailChartData,
    label: 'Карта значимых аспектов',
    labelTooltip:
      'Отображает структуру выбранной категории (или всех категорий сразу). Размер блока зависит от количества оценок, а цвет — от преобладающей тональности. Позволяет точечно идентифицировать конкретные объекты или услуги, вызвавшие наибольшую реакцию пользователей.',
    labelInfoIcon: true,
    type: 'treemap',
    height: 320,
    footer: controls,
  };

  return [masterCard, detailCard];
};
